/**
 * Общая арифметика эффектов подложек (см. scenes/FieldBiomeEffects): случайные
 * числа из внешнего источника, плавные огибающие, ветер, области картинки
 * пейзажа. Чистые функции без Phaser и DOM.
 */

export const TWO_PI = Math.PI * 2;

export const clamp01 = (value) => Math.min(1, Math.max(0, value));
export const lerp = (from, to, share) => from + (to - from) * share;
export const toRadians = (degrees) => (degrees * Math.PI) / 180;

/**
 * Смесь двух красок (0xRRGGBB) по каналам: share 0 — первая, 1 — вторая.
 * Нужна частицам, которые меняют цвет за свою жизнь (остывающий уголек,
 * светлеющий дым): такую краску поза отдает в поле tint
 */
export const mixTints = (from, to, share) => {
  const mixChannel = (shift) => Math.round(lerp((from >> shift) & 255, (to >> shift) & 255, clamp01(share))) << shift;
  return mixChannel(16) | mixChannel(8) | mixChannel(0);
};

/** Плавный переход 0..1 между порогами from и to */
export const smoothstep = (from, to, value) => {
  const share = clamp01((value - from) / (to - from));
  return share * share * (3 - 2 * share);
};

export const randomRange = (rng, min, max) => min + rng() * (max - min);
/** Случайное число из отрезка {min, max} */
export const randomIn = (rng, {min, max}) => randomRange(rng, min, max);
export const randomInt = (rng, min, max) => Math.min(max, Math.floor(randomRange(rng, min, max + 1)));
export const randomSign = (rng) => (rng() < 0.5 ? 1 : -1);
export const pick = (rng, items) => items[Math.min(items.length - 1, Math.floor(rng() * items.length))];

/**
 * Число 0..1, которое выглядит случайным, но зависит только от двух аргументов.
 * Нужно там, где случайность должна быть функцией времени: частица, которая
 * раз за разом рождается заново, на каждом круге берет новое место — и при
 * этом ее поза остается чистой функцией прошедшего времени.
 */
export const hash01 = (first, second = 0) => {
  const value = Math.sin(first * 12.9898 + second * 78.233) * 43758.5453;
  return value - Math.floor(value);
};

/**
 * Выбор по весам.
 * @param {Function} rng
 * @param {Array} items
 * @param {Function} resolveWeight — вес элемента (неотрицательный)
 * @returns {*} элемент; undefined — выбирать не из чего
 */
export const pickWeighted = (rng, items, resolveWeight) => {
  const weights = items.map((item) => Math.max(0, resolveWeight(item)));
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  if (!(totalWeight > 0)) {
    return undefined;
  }
  let roll = rng() * totalWeight;
  for (let index = 0; index < items.length; index += 1) {
    roll -= weights[index];
    if (roll < 0) {
      return items[index];
    }
  }
  return items[items.length - 1];
};

/**
 * Огибающая жизни: плавно проступает за fadeInShare от срока, держится,
 * плавно тает за fadeOutShare. За пределами срока — ноль.
 *
 * @param {number} life — доля прожитого срока, 0..1
 * @returns {number} 0..1
 */
export const resolveLifeEnvelope = (life, fadeInShare, fadeOutShare) => {
  if (!(life > 0) || !(life < 1)) {
    return 0;
  }
  const fadeIn = fadeInShare > 0 ? smoothstep(0, fadeInShare, life) : 1;
  const fadeOut = fadeOutShare > 0 ? smoothstep(0, fadeOutShare, 1 - life) : 1;
  return Math.min(fadeIn, fadeOut);
};

/**
 * Единичный вектор ветра матча — того же, что несет облака (см.
 * resolveCloudDrift): слева направо или справа налево с небольшим наклоном.
 *
 * @param {{direction: number, tiltRad: number}} drift
 * @returns {{x: number, y: number}}
 */
export const resolveWindVector = (drift) => ({
  x: Math.cos(drift?.tiltRad ?? 0) * (drift?.direction ?? 1),
  y: Math.sin(drift?.tiltRad ?? 0),
});

/**
 * Насколько точка внутри овала: 1 — внутри, 0 — за его кромкой, между ними —
 * плавный спад шириной fade (в долях полуосей; 0.2 — спад на последней пятой).
 *
 * @param {{x: number, y: number, radiusX: number, radiusY: number}} oval
 * @param {number} x
 * @param {number} y
 * @param {number} [fade]
 * @returns {number} 0..1
 */
export const resolveOvalFade = (oval, x, y, fade = 0.2) => {
  if (!oval || !(oval.radiusX > 0) || !(oval.radiusY > 0)) {
    return 1;
  }
  const distance = Math.hypot((x - oval.x) / oval.radiusX, (y - oval.y) / oval.radiusY);
  return 1 - smoothstep(1 - fade, 1, distance);
};

/** Случайная точка внутри овала (равномерно по площади); scale сжимает овал */
export const pickPointInOval = (rng, oval, scale = 1) => {
  const angle = rng() * TWO_PI;
  const distance = Math.sqrt(rng()) * scale;
  return {
    x: oval.x + Math.cos(angle) * oval.radiusX * distance,
    y: oval.y + Math.sin(angle) * oval.radiusY * distance,
  };
};

/** Случайная точка прямоугольника */
export const pickPointInBox = (rng, box) => ({
  x: randomRange(rng, box.left, box.right),
  y: randomRange(rng, box.top, box.bottom),
});

/** Пересечение прямоугольников; null — не пересекаются */
export const intersectBoxes = (first, second) => {
  const box = {
    left: Math.max(first.left, second.left),
    right: Math.min(first.right, second.right),
    top: Math.max(first.top, second.top),
    bottom: Math.min(first.bottom, second.bottom),
  };
  return box.right > box.left && box.bottom > box.top ? box : null;
};

/** Прямоугольник, раздвинутый во все стороны на margin */
export const expandBox = (box, margin) => ({
  left: box.left - margin, right: box.right + margin, top: box.top - margin, bottom: box.bottom + margin,
});

/**
 * Записать позу частицы в готовый объект: расчет идет каждый кадр на каждую
 * частицу, и новый объект на каждую был бы мусором.
 *
 * @returns {Object} out
 */
export const writePose = (out, x, y, alpha, width, height, rotation = 0) => {
  out.x = x;
  out.y = y;
  out.alpha = alpha;
  out.width = width;
  out.height = height;
  out.rotation = rotation;
  out.tint = undefined;
  return out;
};

/** Поза «частицы не видно» */
export const writeHiddenPose = (out) => writePose(out, 0, 0, 0, 0, 0, 0);
